import { Capacitor, registerPlugin } from '@capacitor/core';
import { readStoredAuth } from '../api/authApi.js';
import { requestWithFallback } from '../api/httpClient.js';
import { getOfflineStorageDriver, getEffectiveStorageEngine, OFFLINE_STORES } from './offlineStorageAdapter.js';
import { getNetworkOnlineStatus } from '../mobile/networkService.js';

const DataUsage = registerPlugin('DataUsage');
export const DATA_USAGE_CHANGED = 'data-usage:changed';
export const DATA_USAGE_PERMISSION_REQUIRED = 'data-usage:permission-required';
const store = OFFLINE_STORES.DATA_USAGE;
let capturePromise = null, uploadPromise = null;
const nativeAndroid = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
export const canDataUsage = (user, action) => Boolean(user?.empresa_id && user?.permissions?.includes('access.telemetria_datos') && user.permissions.includes(`telemetria_datos.${action}`));
const owner = user => `${user?.empresa_id}:${user?.id}`;
const changed = (confirmed = 0) => { if (typeof window !== 'undefined') { const event = new Event(DATA_USAGE_CHANGED); event.confirmed = confirmed; window.dispatchEvent(event); } };
export function dataUsagePeriod(at, timeZone) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).formatToParts(new Date(at)).map(p => [p.type, p.value]));
    return `${parts.year}-${parts.month}`;
}
export function dataUsageIsDue(ack, now, timeZone) {
    return !ack || now - ack.at >= 8 * 3600000 || ack.period !== dataUsagePeriod(now, timeZone);
}
async function storage() {
    const driver = getOfflineStorageDriver(); await driver.init();
    if (getEffectiveStorageEngine().engine === 'memory') throw new Error('No se pudo abrir el almacenamiento del consumo.');
    return driver;
}

export async function ensureDataUsagePermission(user) {
    if (!nativeAndroid() || !canDataUsage(user, 'registrar_propio')) return;
    const permission = await DataUsage.checkUsagePermission();
    if (permission.granted) return;
    window.dispatchEvent(new Event(DATA_USAGE_PERMISSION_REQUIRED));
    const error = new Error('Para iniciar jornada habilitá el acceso a datos de uso en los ajustes del dispositivo.');
    error.code = 'DATA_USAGE_PERMISSION_REQUIRED'; throw error;
}
export async function openDataUsageSettings() { if (nativeAndroid()) await DataUsage.requestUsagePermission(); }

export async function readDataUsageQueue(user) {
    if (!canDataUsage(user, 'registrar_propio')) return [];
    const driver = await storage();
    return (await driver.getAll(store, null, { userId: Number(user.id) })).filter(row => row.kind === 'capture' && row.scope === owner(user));
}

export function uploadDataUsageQueue() {
    if (uploadPromise) return uploadPromise;
    uploadPromise = (async () => {
        if (!nativeAndroid()) return { confirmed: 0, pending: 0 };
        const session = await readStoredAuth();
        if (!session?.token || !canDataUsage(session.user, 'registrar_propio')) return { confirmed: 0, pending: 0 };
        const scope = owner(session.user), driver = await storage();
        const rows = await readDataUsageQueue(session.user); let confirmed = 0, failure = null, retryable = false;
        if (!rows.length) return { confirmed: 0, pending: 0 };
        if (!await getNetworkOnlineStatus()) return { confirmed: 0, pending: rows.length, failure: 'Sin conexión. La captura queda guardada para enviarse al recuperar red.' };
        for (const row of rows) {
            try {
                const current = await readStoredAuth();
                if (owner(current?.user) !== scope || !current?.token || !canDataUsage(current.user, 'registrar_propio')) break;
                const { data } = await requestWithFallback({ moduleName: 'telemetria', endpoint: '/react/telemetria-consumo-datos', method: 'post', token: current.token, headers: { 'X-Telemetria-Owner': scope }, data: row.payload, timeout: 20000 });
                const periods = row.payload.months.map(m => m.periodo).sort();
                if (!data?.ok || data.confirmed !== row.id || JSON.stringify([...data.periodos || []].sort()) !== JSON.stringify(periods)) throw new Error('El servidor no confirmó la captura.');
                const after = await readStoredAuth();
                if (owner(after?.user) !== scope || !canDataUsage(after.user, 'registrar_propio')) break;
                const ackId = `ack:${scope}`;
                const previous = await driver.get(store, ackId);
                const captureAt = Date.parse(row.payload.captured_at);
                if (!previous || captureAt >= previous.capturedAt) {
                    await driver.put(store, { id: ackId, kind: 'ack', userId: Number(session.user.id), scope, at: Date.now(), capturedAt: captureAt,
                        period: dataUsagePeriod(captureAt, row.payload.zona_horaria) });
                }
                await driver.compareAndDelete(store, row.id, actual => actual?.scope === scope && JSON.stringify(actual.payload) === JSON.stringify(row.payload));
                confirmed++;
            } catch (error) {
                const status = error?.response?.status;
                retryable = !status || status >= 500 || status === 408 || status === 429;
                failure = status === 401 ? 'La sesión venció. Volvé a iniciar sesión.' : status === 403 ? 'Tu cuenta no tiene permiso para enviar esta captura.' :
                    status === 422 || status === 409 ? 'El servidor rechazó la captura. Se conserva para revisión.' : 'Sin confirmación del servidor. La captura sigue guardada para reintentar.';
                break;
            }
        }
        changed(confirmed);
        return { confirmed, pending: (await readDataUsageQueue(session.user)).length, failure, retryable };
    })().finally(() => { uploadPromise = null; });
    return uploadPromise;
}

export function syncDataUsageIfDue({ force = false, trigger = 'shift_start', expectedUserId = null } = {}) {
    if (capturePromise) return force ? capturePromise.catch(() => {}).then(() => syncDataUsageIfDue({ force, trigger, expectedUserId })) : capturePromise;
    capturePromise = (async () => {
        if (!nativeAndroid()) return { skipped: true };
        const session = await readStoredAuth();
        if (!canDataUsage(session?.user, 'registrar_propio') || expectedUserId !== null && String(session.user.id) !== String(expectedUserId)) return { skipped: true };
        const scope = owner(session.user), timezone = session.user.empresa?.regional?.zona_horaria;
        if (!timezone) throw new Error('La zona horaria de la empresa no está disponible.');
        const driver = await storage(), ack = await driver.get(store, `ack:${scope}`);
        if (!force && !dataUsageIsDue(ack, Date.now(), timezone)) return { skipped: true, ...(await uploadDataUsageQueue()) };
        const report = await DataUsage.getMonthlyUsageReport({ monthsBack: 2, timeZone: timezone });
        const after = await readStoredAuth();
        if (owner(after?.user) !== scope || !canDataUsage(after.user, 'registrar_propio')) return { skipped: true };
        const id = crypto.randomUUID();
        await driver.put(store, { id, kind: 'capture', userId: Number(session.user.id), scope, payload: { ...report, capture_uuid: id, trigger } });
        changed();
        return uploadDataUsageQueue();
    })().finally(() => { capturePromise = null; });
    return capturePromise;
}
